import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Plus, Search, Edit, Trash2, Filter, Package, CheckCircle, AlertTriangle, XCircle, ArrowLeft, ChevronRight, Grid, List, Eye } from 'lucide-react';
import Card from '../../components/Card';
import Button from '../../components/Button';
import Input from '../../components/Input';
import Select from '../../components/Select';
import Table from '../../components/Table';
import Modal from '../../components/Modal';
import { apiService } from '../../services/apiService';
import { formatDate, getEquipmentStatus, getStatusColor, getStatusLabel, renderCell, coalesce, renderCellDate } from '../../utils/helpers';
import { EQUIPMENT_TYPES, EQUIPMENT_TYPE_LABELS } from '../../utils/constants';

const EquipmentManagement = () => {
  const navigate = useNavigate();
  const { siteId } = useParams(); // Get siteId from URL params
  const [equipment, setEquipment] = useState([]);
  const [filteredEquipment, setFilteredEquipment] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [deleteModal, setDeleteModal] = useState({ open: false, equipment: null });
  const [siteInfo, setSiteInfo] = useState(null);
  const [viewMode, setViewMode] = useState('table'); // 'table' or 'card' - default to table

  useEffect(() => {
    loadEquipment();
    if (siteId) {
      loadSiteInfo();
    }
  }, [siteId]);

  useEffect(() => {
    filterEquipment();
  }, [searchQuery, typeFilter, equipment]);

  const loadSiteInfo = async () => {
    try {
      const data = await apiService.getSite(siteId);
      setSiteInfo(data);
    } catch (error) {
      console.error('Failed to load site info:', error);
    }
  };

  const loadEquipment = async () => {
    try {
      setLoading(true);
      const data = await apiService.getEquipment(null, siteId);
      setEquipment(data);
    } catch (error) {
      console.error('Failed to load equipment:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterEquipment = () => {
    let filtered = [...equipment];

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (item) =>
          item.serialNumber?.toLowerCase().includes(query) ||
          item.type?.toLowerCase().includes(query) ||
          item.clientName?.toLowerCase().includes(query) ||
          item.siteName?.toLowerCase().includes(query)
      );
    }

    if (typeFilter) {
      filtered = filtered.filter((item) => item.type === typeFilter);
    }

    setFilteredEquipment(filtered);
  };

  const handleDelete = async () => {
    try {
      await apiService.deleteEquipment(deleteModal.equipment.id);
      setEquipment(equipment.filter((e) => e.id !== deleteModal.equipment.id));
      setDeleteModal({ open: false, equipment: null });
    } catch (error) {
      console.error('Failed to delete equipment:', error);
    }
  };

  const columns = [
    {
      header: 'Numéro de série',
      accessor: 'serialNumber',
      render: (row) => (
        <div>
          <p className="font-medium text-gray-900">{renderCell(row.serialNumber)}</p>
          <p className="text-sm text-gray-500">{renderCell(coalesce(EQUIPMENT_TYPE_LABELS[row.type], row.type))}</p>
        </div>
      ),
    },
    {
      header: 'Client',
      accessor: 'clientName',
      render: (row) => <span className="text-sm text-gray-700">{renderCell(row.clientName)}</span>,
    },
    {
      header: 'Site',
      accessor: 'siteName',
      render: (row) => <span className="text-sm text-gray-700">{renderCell(coalesce(row.siteName, 'Site Principal'))}</span>,
    },
    {
      header: 'Emplacement',
      accessor: 'location',
      render: (row) => <span className="text-sm text-gray-700">{renderCell(row.location)}</span>,
    },
    {
      header: 'Prochaine maintenance',
      accessor: 'nextMaintenanceDate',
      render: (row) => (
        <span className="text-sm text-gray-700">{renderCellDate(row.nextMaintenanceDate)}</span>
      ),
    },
    {
      header: 'État',
      accessor: 'status',
      render: (row) => {
        const status = getEquipmentStatus(row);
        return (
          <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(status)}`}>
            {getStatusLabel(status)}
          </span>
        );
      },
    },
    {
      header: 'Actions',
      accessor: 'id',
      render: (row) => (
        <div className="flex items-center gap-2">
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={() => navigate(`/equipment/edit/${row.id}`)}
            className="p-2.5 text-blue-600 hover:bg-blue-50 rounded-xl transition-all hover:shadow-md"
            title="Modifier"
          >
            <Edit className="w-4 h-4" />
          </motion.button>
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={() => setDeleteModal({ open: true, equipment: row })}
            className="p-2.5 text-red-600 hover:bg-red-50 rounded-xl transition-all hover:shadow-md"
            title="Supprimer"
          >
            <Trash2 className="w-4 h-4" />
          </motion.button>
        </div>
      ),
    },
  ];

  const equipmentTypeOptions = [
    { value: '', label: 'Tous les types' },
    { value: EQUIPMENT_TYPES.EXTINGUISHER, label: 'Extincteur' },
    { value: EQUIPMENT_TYPES.RIA, label: 'RIA' },
    { value: EQUIPMENT_TYPES.BDI, label: 'BDI' },
    { value: EQUIPMENT_TYPES.CABINET, label: 'Armoire' },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  // Calculate stats
  const totalEquipment = equipment.length;
  const operationalEquipment = equipment.filter(eq => getEquipmentStatus(eq) === 'operational').length;
  const maintenanceRequired = equipment.filter(eq => {
    const status = getEquipmentStatus(eq);
    return status === 'expiration_warning' || status === 'maintenance_required';
  }).length;
  const expiredEquipment = equipment.filter(eq => getEquipmentStatus(eq) === 'expired').length;

  return (
    <div className="space-y-3 sm:space-y-4 md:space-y-6">
      {/* Breadcrumb Navigation */}
      {siteId && siteInfo && (
        <div className="flex items-center space-x-2 text-sm text-gray-600">
          <button 
            onClick={() => navigate('/clients')}
            className="hover:text-red-600 transition-colors"
          >
            Clients
          </button>
          <ChevronRight className="w-4 h-4" />
          <button 
            onClick={() => navigate(`/clients/${siteInfo.clientId}/sites`)}
            className="hover:text-red-600 transition-colors"
          >
            {siteInfo.clientName}
          </button>
          <ChevronRight className="w-4 h-4" />
          <span className="font-medium text-gray-900">{siteInfo.name}</span>
        </div>
      )}

      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-4"
      >
        <div>
          {siteId && (
            <Button
              variant="outline"
              icon={ArrowLeft}
              onClick={() => siteInfo ? navigate(`/clients/${siteInfo.clientId}/sites`) : navigate('/clients')}
              className="mb-3"
              size="sm"
            >
              Retour aux sites
            </Button>
          )}
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-gray-900">
            {siteId && siteInfo ? `Équipements - ${siteInfo.name}` : 'Gestion des Équipements'}
          </h1>
          <p className="text-sm sm:text-base text-gray-600 mt-1 sm:mt-2 font-medium">
            {siteId && siteInfo ? `${siteInfo.clientName}` : 'Gérez tous vos équipements de sécurité incendie'}
          </p>
        </div>
        <div className="flex items-center gap-2 sm:gap-3 flex-wrap w-full sm:w-auto">
          <Button
            variant={viewMode === 'table' ? 'primary' : 'outline'}
            size="sm"
            icon={List}
            onClick={() => setViewMode('table')}
          >
            Tableau
          </Button>
          <Button
            variant={viewMode === 'card' ? 'primary' : 'outline'}
            size="sm"
            icon={Grid}
            onClick={() => setViewMode('card')}
          >
            Cartes
          </Button>
          <Button
            variant="outline"
            onClick={() => navigate('/equipment/add-extinguisher')}
            icon={Plus}
            className="flex-1 sm:flex-none"
          >
            Extincteur
          </Button>
          <Button
            variant="primary"
            icon={Plus}
            onClick={() => navigate('/equipment/add-other')}
            className="flex-1 sm:flex-none"
          >
            Autre Équipement
          </Button>
        </div>
      </motion.div>

      {/* Stats Bar */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3 md:gap-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0 }}
          whileHover={{ y: -3, scale: 1.02 }}
          className="bg-white rounded-lg sm:rounded-xl shadow-lg border border-gray-100 p-3 sm:p-4 hover:shadow-2xl hover:border-red-200 transition-all"
        >
          <div>
            <div className="flex items-start justify-between mb-2 sm:mb-3">
              <div className="p-2 sm:p-2.5 bg-gradient-to-br from-red-500 to-red-600 rounded-lg sm:rounded-xl shadow-lg shadow-red-500/30">
                <Package className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
              </div>
            </div>
            <p className="text-xs sm:text-sm font-bold text-gray-500 uppercase tracking-wide mb-1">Total Équipements</p>
            <p className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-gray-900 leading-none">{totalEquipment}</p>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          whileHover={{ y: -3, scale: 1.02 }}
          className="bg-white rounded-lg sm:rounded-xl shadow-lg border border-gray-100 p-3 sm:p-4 hover:shadow-2xl hover:border-green-200 transition-all"
        >
          <div>
            <div className="flex items-start justify-between mb-2 sm:mb-3">
              <div className="p-2 sm:p-2.5 bg-gradient-to-br from-green-500 to-emerald-600 rounded-lg sm:rounded-xl shadow-lg shadow-green-500/30">
                <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
              </div>
              <span className="text-xs sm:text-sm font-bold text-green-600 bg-green-50 px-2 sm:px-2.5 py-0.5 sm:py-1 rounded-lg">
                {totalEquipment > 0 ? Math.round((operationalEquipment / totalEquipment) * 100) : 0}%
              </span>
            </div>
            <p className="text-xs sm:text-sm font-bold text-gray-500 uppercase tracking-wide mb-1">Terminés</p>
            <p className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-gray-900 leading-none">{operationalEquipment}</p>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          whileHover={{ y: -3, scale: 1.02 }}
          className="bg-white rounded-lg sm:rounded-xl shadow-lg border border-gray-100 p-3 sm:p-4 hover:shadow-2xl hover:border-amber-200 transition-all"
        >
          <div>
            <div className="flex items-start justify-between mb-2 sm:mb-3">
              <div className="p-2 sm:p-2.5 bg-gradient-to-br from-amber-500 to-orange-600 rounded-lg sm:rounded-xl shadow-lg shadow-amber-500/30">
                <AlertTriangle className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
              </div>
              <span className="text-xs sm:text-sm font-bold text-amber-600 bg-amber-50 px-2 sm:px-2.5 py-0.5 sm:py-1 rounded-lg">
                {totalEquipment > 0 ? Math.round((maintenanceRequired / totalEquipment) * 100) : 0}%
              </span>
            </div>
            <p className="text-xs sm:text-sm font-bold text-gray-500 uppercase tracking-wide mb-1">Maintenance</p>
            <p className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-gray-900 leading-none">{maintenanceRequired}</p>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          whileHover={{ y: -3, scale: 1.02 }}
          className="bg-white rounded-lg sm:rounded-xl shadow-lg border border-gray-100 p-3 sm:p-4 hover:shadow-2xl hover:border-rose-200 transition-all"
        >
          <div>
            <div className="flex items-start justify-between mb-2 sm:mb-3">
              <div className="p-2 sm:p-2.5 bg-gradient-to-br from-rose-500 to-rose-600 rounded-lg sm:rounded-xl shadow-lg shadow-rose-500/30">
                <XCircle className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
              </div>
              <span className="text-xs sm:text-sm font-bold text-rose-600 bg-rose-50 px-2 sm:px-2.5 py-0.5 sm:py-1 rounded-lg">
                {totalEquipment > 0 ? Math.round((expiredEquipment / totalEquipment) * 100) : 0}%
              </span>
            </div>
            <p className="text-xs sm:text-sm font-bold text-gray-500 uppercase tracking-wide mb-1">Expirés</p>
            <p className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-gray-900 leading-none">{expiredEquipment}</p>
          </div>
        </motion.div>
      </div>

      {/* Search and Filters */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
      >
        <Card>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
            <div className="flex-1">
              <Input
                placeholder="Rechercher un équipement..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                icon={Search}
              />
            </div>
            <div className="sm:w-64">
              <Select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                options={equipmentTypeOptions}
              />
            </div>
            <Button variant="outline" icon={Filter} className="sm:w-auto">
              Filtres
            </Button>
          </div>
        </Card>
      </motion.div>

      {/* Equipment Table View */}
      {viewMode === 'table' && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <Card>
            <div className="mb-6 flex items-center justify-between">
              <h2 className="text-2xl font-bold text-gray-900">
                {filteredEquipment.length} <span className="text-gray-500 font-normal">équipement(s)</span>
              </h2>
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500 font-medium">Total: </span>
                <span className="px-3 py-1 bg-red-100 text-red-700 rounded-xl font-bold text-sm">
                  {filteredEquipment.length}
                </span>
              </div>
            </div>
            <Table columns={columns} data={filteredEquipment} />
          </Card>
        </motion.div>
      )}

      {/* Equipment Card View */}
      {viewMode === 'card' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredEquipment.map((equip, index) => {
            const status = getEquipmentStatus(equip);
            return (
              <motion.div
                key={equip.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <Card className="hover:shadow-lg transition-shadow">
                  <div className="space-y-4">
                    {/* Header */}
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <div className="text-3xl">{equip.type === 'extinguisher' ? '🧯' : '📦'}</div>
                        <div>
                          <h3 className="text-lg font-bold text-gray-900">{EQUIPMENT_TYPE_LABELS[equip.type]}</h3>
                          <p className="text-sm text-gray-500">{equip.serialNumber}</p>
                        </div>
                      </div>
                      <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(status)}`}>
                        {getStatusLabel(status)}
                      </span>
                    </div>

                    {/* Details */}
                    <div className="space-y-2 text-sm">
                      {equip.clientName && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">Client:</span>
                          <span className="font-medium text-gray-900">{equip.clientName}</span>
                        </div>
                      )}
                      {equip.siteName && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">Site:</span>
                          <span className="font-medium text-gray-900">{equip.siteName}</span>
                        </div>
                      )}
                      {equip.location && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">Emplacement:</span>
                          <span className="font-medium text-gray-900">{equip.location}</span>
                        </div>
                      )}
                      {equip.nextMaintenanceDate && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">Prochaine vérif:</span>
                          <span className="font-medium text-gray-900">{formatDate(equip.nextMaintenanceDate)}</span>
                        </div>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2 pt-2 border-t border-gray-200">
                      <Button
                        variant="outline"
                        size="sm"
                        icon={Eye}
                        onClick={() => navigate(`/equipment/edit/${equip.id}`)}
                        className="flex-1"
                      >
                        Voir
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        icon={Edit}
                        onClick={() => navigate(`/equipment/edit/${equip.id}`)}
                      />
                      <Button
                        variant="outline"
                        size="sm"
                        icon={Trash2}
                        onClick={() => setDeleteModal({ open: true, equipment: equip })}
                        className="text-red-600 hover:bg-red-50"
                      />
                    </div>
                  </div>
                </Card>
              </motion.div>
            );
          })}
        </div>
      )}

      {/* Empty State */}
      {filteredEquipment.length === 0 && (
        <Card>
          <div className="text-center py-12">
            <Package className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Aucun équipement trouvé
            </h3>
            <p className="text-gray-600">
              {searchQuery || typeFilter ? 'Essayez un autre filtre' : 'Commencez par ajouter un équipement'}
            </p>
          </div>
        </Card>
      )}

      {/* Delete Modal */}
      <Modal
        isOpen={deleteModal.open}
        onClose={() => setDeleteModal({ open: false, equipment: null })}
        title="Supprimer l'équipement"
      >
        <div className="space-y-4">
          <p className="text-gray-600">
            Êtes-vous sûr de vouloir supprimer l'équipement{' '}
            <strong>{deleteModal.equipment?.serialNumber}</strong> ? Cette action est irréversible.
          </p>
          <div className="flex items-center justify-end space-x-3">
            <Button
              variant="secondary"
              onClick={() => setDeleteModal({ open: false, equipment: null })}
            >
              Annuler
            </Button>
            <Button variant="danger" onClick={handleDelete}>
              Supprimer
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default EquipmentManagement;

